import StudioEditor from "@grapesjs/studio-sdk/react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useCallback } from "react";

import type { SetupStyling, SetupOperation } from "@/types/onboarding";
import type { CreateEditorOptions } from "@grapesjs/studio-sdk";
import type { Editor, ProjectData } from "grapesjs";

import { updateDataSources } from "@/features/Onboarding/utils/editorStyleUpdater";
import CustomDataComponents from "@/grapesPlugins/email/CustomDataComponents";
import DataSourcesAdd from "@/grapesPlugins/email/DataSourcesAdd";
import DataSourcesImporterExporter from "@/grapesPlugins/email/DataSourcesImporterExporter";
import DataSourcesUpdater from "@/grapesPlugins/email/DataSourcesUpdater";
import DataSourcesUpgradeBlocks from "@/grapesPlugins/email/DataSourcesUpgradeBlocks";
import FixEmailBlockPreviews from "@/grapesPlugins/email/FixEmailBlockPreviews";
import MjClassTrait from "@/grapesPlugins/email/MjClassTrait";
import makeEmailProjectFromBodyContent from "@/grapesPlugins/email/utils/makeEmailProjectFromBodyContent";
import { CloudUploadIcon, ContentCopyIcon, DownloadIcon } from "@/icons";

export const Route = createFileRoute("/_auth/preview-test")({
  component: PreviewTestComponent,
});

function PreviewTestComponent() {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [editorReady, setEditorReady] = useState(false);
  const [html, setHtml] = useState("");
  const [projectData, setProjectData] = useState("");
  const [mjmlHtml, setMjmlHtml] = useState("");
  const [mjmlToGrapesProject, setMjmlToGrapesProject] = useState<ProjectData>(
    {},
  );
  const [mjmlToGrapesProjectPostPlugins, setMjmlToGrapesProjectPostPlugins] =
    useState<ProjectData>({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [originalFileName, setOriginalFileName] = useState<string>("");

  const plugins = useMemo(
    () => [
      FixEmailBlockPreviews,
      MjClassTrait,
      DataSourcesImporterExporter,
      DataSourcesAdd,
      DataSourcesUpdater,
      DataSourcesUpgradeBlocks,
      CustomDataComponents,
      (editor: Editor) => {
        editor.onReady(() => {
          // Loading custom data sources should be done onReady when in browser or after instantiation when in headless
          // https://app.grapesjs.com/docs-sdk/configuration/datasources/overview#programmatic-usage
          editor.runCommand("data-sources-add:default");
          setMjmlToGrapesProjectPostPlugins(editor.getProjectData());
        });
      },
    ],
    [],
  );

  // Process MJML file content
  const processMjmlFile = useCallback(async (file: File) => {
    setIsProcessingFile(true);

    // Reset all state to prevent issues with previous editor instances
    setEditor(null);
    setEditorReady(false);
    setHtml("");
    setProjectData("");
    setMjmlHtml("");
    setMjmlToGrapesProjectPostPlugins({});
    setOriginalFileName("");

    try {
      const text = await file.text();

      // Extract content inside mj-body tags
      const mjBodyMatch = text.match(/<mj-body[^>]*>([\s\S]*?)<\/mj-body>/i);
      const mjBodyContent = mjBodyMatch ? mjBodyMatch[1].trim() : text;

      if (!mjBodyContent) {
        throw new Error("No mj-body content found in MJML file");
      }

      const projectData = makeEmailProjectFromBodyContent(mjBodyContent);
      setMjmlToGrapesProject(projectData);

      // Store the original filename for download functionality
      setOriginalFileName(file.name);

      console.log("MJML file processed successfully");
    } catch (error) {
      console.error("Failed to process MJML file:", error);
      alert(
        `Error processing MJML file: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setIsProcessingFile(false);
    }
  }, []);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      const mjmlFile = files.find(
        (file) =>
          file.name.toLowerCase().endsWith(".mjml") ||
          file.type === "text/xml" ||
          file.type === "application/xml",
      );

      if (mjmlFile) {
        void processMjmlFile(mjmlFile);
      } else {
        alert("Please drop a valid MJML file (.mjml extension)");
      }
    },
    [processMjmlFile],
  );

  // File input handler for click to upload
  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        void processMjmlFile(file);
      }
    },
    [processMjmlFile],
  );

  // Dummy form data for testing
  const dummyStylingData: SetupStyling = {
    alertBannerBackground: "#e99b7d",
    // alertBannerTextFontWeight: "bold",
    // alertBannerTextTransform: "uppercase",
    bannerBackground: "#e99b7d",
    // bodyBrandFontFamily: "",
    bodyFontFamily: "Arial, Helvetica, sans-serif",
    buttonPrimaryBg: "#d0502a",
    buttonPrimaryBorderColor: "#e99b7d",
    buttonPrimaryBorderRadius: "4",
    buttonPrimaryBorderStyle: "solid",
    buttonPrimaryBorderWidth: "1",
    buttonPrimaryPadding: "16px 24px",
    buttonPrimaryText: "#fefdfd",
    // buttonPrimaryTextTransform: "",
    buttonSecondaryBg: "#fefdfd",
    buttonSecondaryBorderColor: "#e99b7d",
    buttonSecondaryBorderRadius: "4",
    buttonSecondaryBorderStyle: "solid",
    buttonSecondaryBorderWidth: "1",
    buttonSecondaryPadding: "16px 24px",
    buttonSecondaryText: "#d0502a",
    // buttonSecondaryTextTransform: "",
    buttonTertiaryBg: "#e99b7d",
    buttonTertiaryBorderColor: "#fefdfd",
    buttonTertiaryBorderRadius: "4",
    buttonTertiaryBorderStyle: "solid",
    buttonTertiaryBorderWidth: "1",
    buttonTertiaryPadding: "16px 24px",
    buttonTertiaryText: "#d0502a",
    // buttonTertiaryTextTransform: "",
    canvasBackground: "#fff3ef",
    darkLogo: {
      name: "20250903195027_l5gWElDqDtKOz6Pn_logo.png",
      src: "https://assets.queen.one/sites/ALOW3mL/20250903195027_l5gWElDqDtKOz6Pn_logo.png",
      // type: "application",
    },
    fontSizeDesktopDefault: "18",
    fontSizeDesktopL: "20",
    fontSizeDesktopM: "16",
    fontSizeDesktopOversize: "34",
    fontSizeDesktopS: "14",
    fontSizeDesktopXL: "24",
    fontSizeDesktopXS: "12",
    fontSizeMobileL: "",
    fontSizeMobileM: "",
    fontSizeMobileOversize: "",
    fontSizeMobileS: "",
    fontSizeMobileXL: "",
    fontSizeMobileXS: "",
    // headlineBrandFontFamily: "",
    headlineDarkColor: "#d0502a",
    headlineFontFamily: "Arial, Helvetica, sans-serif",
    headlineFontWeight: "bold",
    headlineLightColor: "#fefdfd",
    // headlineTextTransform: "",
    infoBackground: "#fefdfd",
    lightLogo: {
      name: "20250903195027_l5gWElDqDtKOz6Pn_logo.png",
      src: "https://assets.queen.one/sites/ALOW3mL/20250903195027_l5gWElDqDtKOz6Pn_logo.png",
      // type: "application",
    },
    logoWidth: "35",
    outlineColor: "#e99b7d",
    productBackground: "#d0502a",
    textDarkColor: "#d0502a",
    textLightColor: "#fefdfd",
    textLinkColor: "#d0502a",
    textLinkUnderline: 0,
    wrapperBackground: "#fefdfd",
  };

  const dummyOperationData: SetupOperation = {
    containers: [
      {
        title: "Value Proposition 1",
        body: "This is a sample value proposition based on your domain.",
        asset: null,
      },
      {
        title: "Value Proposition 2",
        body: "Another compelling reason to choose your service.",
        asset: null,
      },
      {
        title: "Value Proposition 3",
        body: "Third key benefit of your offering.",
        asset: null,
      },
      {
        title: "Value Proposition 4",
        body: "Final compelling reason to take action.",
        asset: null,
      },
    ],
    heroAsset: null,
    heroBanner: "Welcome to your-company",
    informationAddress: "123 Business St, City, State",
    informationEmail: "contact@your-company",
    informationName: "Your Business Name",
    informationPhone: "+1 (555) 123-4567",
    links: [
      {
        name: "About Us",
        url: "https://your-company/about",
        asset: null,
      },
      {
        name: "Contact",
        url: "https://your-company/contact",
        asset: null,
      },
    ],
    messagingAdditional: "Additional messaging based on your domain analysis.",
    messagingDisclaimer: "Terms and conditions apply.",
    messagingFreeShipping: "Free shipping on orders over $50.",
    messagingTerms: "Standard terms and conditions.",
    socials: [
      {
        name: "Facebook",
        url: "https://facebook.com/yourbusiness",
        asset: null,
      },
      {
        name: "Twitter",
        url: "https://twitter.com/yourbusiness",
        asset: null,
      },
      {
        name: "Instagram",
        url: "https://instagram.com/yourbusiness",
        asset: null,
      },
      {
        name: "LinkedIn",
        url: "https://linkedin.com/company/yourbusiness",
        asset: null,
      },
      {
        name: "YouTube",
        url: "https://youtube.com/yourbusiness",
        asset: null,
      },
    ],
    title: "Welcome to your-company",
    currencySymbol: "$",
  };

  const dummyCopyData: Record<string, string> = {
    body_copy_1:
      "Warning: This product may cause uncontrollable dancing and an inexplicable urge to high-five strangers",
    body_copy_2:
      "Our team of highly trained hamsters worked around the clock to bring you this magnificently ridiculous creation",
    bullet_point_1: "• 99.9% guaranteed to make your neighbor's cat jealous",
    bullet_point_2:
      "• Comes with a free imaginary friend (batteries not included)",
    bullet_point_3:
      "• May cause spontaneous bursts of confident karaoke performances",
    call_to_action_1: "Click Here to Join the Chaos!",
    call_to_action_2: "Embrace Your Inner Weirdo",
    call_to_action_3: "Yes, I Want to Be Fabulous!",
    headline: "The Most Ridiculously Awesome Thing You Never Knew You Needed",
    Subheader1:
      "Scientifically proven to increase your coolness factor by 247%",
    Subheader2:
      "Warning: Side effects may include excessive giggling and jazz hands",
  };

  const studioEditorOptions: CreateEditorOptions | null = useMemo(() => {
    // Only create editor options when we have valid project data
    if (!mjmlToGrapesProject || Object.keys(mjmlToGrapesProject).length === 0) {
      return null;
    }

    return {
      theme: "light",
      licenseKey: import.meta.env.VITE_OPTINS_STUDIO_LICENSE as string,
      project: {
        type: "email",
        default: mjmlToGrapesProject,
      },
      onReady: (editor: Editor) => {
        window.grapesEditor = editor;
        setEditor(editor);
        setEditorReady(true);
      },
      storage: {
        type: "self",
        onSave: async () => Promise.resolve(),
        project: mjmlToGrapesProject,
      },
      components: {
        toolbar: () => [],
        contextMenu: () => [],
      },
      gjsOptions: {
        // headless: true,
        canvas: {
          frameStyle: `
            html { 
              background-color: white;
            }
            body {
              margin: 0;
              padding: 0;
            }
            table { border-collapse: separate !important }
          `,
          customSpots: {
            spacing: false,
          },
        },
        protectedCss: "",
      },
      globalStyles: { default: [] },
      plugins,
    };
  }, [mjmlToGrapesProject, plugins]);

  const renderPreview = () => {
    console.log("Starting rendering");

    if (!editor) {
      console.error("Editor not initialized, waiting for it to be ready...");
      // Wait a bit for the editor to initialize
      setTimeout(() => {
        if (editor) {
          console.log("Editor is now ready, retrying...");
          renderPreview();
        } else {
          console.error("Editor still not ready after timeout");
        }
      }, 1000);
      return;
    }

    try {
      console.log("Editor loaded, applying form data...");

      updateDataSources(editor, {
        styling: dummyStylingData,
        operation: dummyOperationData,
        generated_content: dummyCopyData,
      });

      console.log("Form data applied, getting HTML...");

      // Get the rendered HTML from headless editor
      const renderedHtml = editor.getHtml();
      const projectDataObj = editor.getProjectData();

      // Run the mjml-code-to-html command
      const mjmlResult = editor.runCommand("mjml-code-to-html") as {
        html: string;
        json: unknown;
        errors: unknown[];
      };
      console.log("MJML command result:", mjmlResult);

      console.log("HTML length:", renderedHtml?.length);
      console.log("Project data:", projectDataObj);

      // Always show the actual results from GrapesJS
      setHtml(renderedHtml || "No MJML generated");
      setProjectData(
        JSON.stringify(projectDataObj, null, 2) || "No project data",
      );
      setMjmlHtml(mjmlResult?.html || "No HTML generated");

      console.log("Preview rendered successfully");
    } catch (error) {
      console.error("Error rendering preview:", error);
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      console.log(`${type} copied to clipboard`);
    } catch (err) {
      console.error(`Failed to copy ${type}:`, err);
    }
  };

  const downloadAsFile = (
    content: string,
    filename: string,
    contentType: string = "text/plain",
  ) => {
    try {
      const blob = new Blob([content], { type: contentType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log(`File ${filename} downloaded`);
    } catch (err) {
      console.error(`Failed to download ${filename}:`, err);
    }
  };

  const getBaseFileName = (originalName: string) => {
    return originalName.replace(/\.[^/.]+$/, ""); // Remove extension
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "grid",
        gridTemplateColumns: "60ch 1fr",
      }}
    >
      <Stack sx={{ p: 7, gap: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Onboarding Plugins Test
          </Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Drag and drop an MJML file to export them as grapesjs files. The
            mj-body content will be copied into a standard wrapper that includes
            a standard mj-head component that is linked up to data sources. A
            set of plugins are run to add the data sources and to update MJML
            components to link to those data sources. Certain template strings
            are also converted into data variables like{" "}
            <code>{"{{ operation.data.brand_name }}"}</code>.
          </Typography>
        </Box>

        {/* Drag and Drop Zone */}
        {!mjmlToGrapesProject ||
        Object.keys(mjmlToGrapesProject).length === 0 ? (
          <Paper
            sx={{
              border: `2px dashed ${isDragOver ? "primary.main" : "grey.300"}`,
              borderRadius: 2,
              p: 5,
              textAlign: "center",
              backgroundColor: isDragOver ? "action.hover" : "background.paper",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              minHeight: 200,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              mb: 4,
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("mjml-file-input")?.click()}
          >
            <input
              id="mjml-file-input"
              type="file"
              accept=".mjml,.xml"
              style={{ display: "none" }}
              onChange={handleFileInput}
            />
            <CloudUploadIcon
              sx={{
                fontSize: 48,
                color: isDragOver ? "primary.main" : "grey.400",
                mb: 3,
              }}
            />
            <Typography variant="h6" gutterBottom>
              {isProcessingFile
                ? "Processing MJML file..."
                : "Drop MJML file here"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isProcessingFile
                ? "Extracting mj-body content and converting to GrapesJS project..."
                : "or click to browse for .mjml files"}
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ p: 3, mb: 4, backgroundColor: "success.light" }}>
            <Typography variant="body1" color="success.dark">
              ✅ MJML file loaded successfully! The editor is ready below.
            </Typography>
          </Paper>
        )}
        {mjmlToGrapesProject && Object.keys(mjmlToGrapesProject).length > 0 && (
          <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
            <Button
              variant="contained"
              onClick={() => {
                void renderPreview();
              }}
              disabled={!editorReady || isProcessingFile}
            >
              Render Preview
            </Button>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ alignSelf: "center" }}
            >
              {editorReady ? "✅ Editor Ready" : "⏳ Initializing Editor..."}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ alignSelf: "center" }}
            >
              {mjmlToGrapesProject &&
              Object.keys(mjmlToGrapesProject).length > 0
                ? "✅ MJML Processed"
                : "⏳ No MJML file loaded"}
            </Typography>
          </Stack>
        )}
        {!!mjmlToGrapesProject && (
          <Paper
            sx={{
              display: "grid",
              gridTemplateRows: "max-content 1fr",
              flex: 1,
              minWidth: "300px",
              maxHeight: "270px",
              p: 3,
              overflow: "auto",
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                Your MJML file with standard wrapper
              </Typography>
              <Stack direction="row" spacing={2}>
                <Tooltip title="Copy MJML Template to clipboard">
                  <IconButton
                    size="small"
                    onClick={() => {
                      void copyToClipboard(
                        JSON.stringify(mjmlToGrapesProject, null, 2),
                        "MJML Template",
                      );
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Box
              component="pre"
              sx={{
                fontSize: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                overflow: "auto",
                margin: 0,
                p: 3,
              }}
            >
              {JSON.stringify(mjmlToGrapesProject, null, 2)}
            </Box>
          </Paper>
        )}
        {!!mjmlToGrapesProjectPostPlugins &&
          Object.keys(mjmlToGrapesProjectPostPlugins).length > 0 && (
            <Paper
              sx={{
                display: "grid",
                gridTemplateRows: "max-content 1fr",
                flex: 1,
                minWidth: "300px",
                maxHeight: "270px",
                p: 3,
                overflow: "auto",
                mb: 4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6">
                    Your MJML file with standard wrapper
                  </Typography>
                  <Typography variant="body1">
                    After data sources and plugins are applied
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Copy Post-Plugins Project to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        void copyToClipboard(
                          JSON.stringify(
                            mjmlToGrapesProjectPostPlugins,
                            null,
                            2,
                          ),
                          "Post-Plugins Project",
                        );
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Post-Plugins Project as JSON">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const baseFileName = getBaseFileName(originalFileName);
                        downloadAsFile(
                          JSON.stringify(
                            mjmlToGrapesProjectPostPlugins,
                            null,
                            2,
                          ),
                          `${baseFileName}.grapesjs`,
                          "application/json",
                        );
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
              <Box
                component="pre"
                sx={{
                  fontSize: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  overflow: "auto",
                  margin: 0,
                  p: 3,
                }}
              >
                {JSON.stringify(mjmlToGrapesProjectPostPlugins, null, 2)}
              </Box>
            </Paper>
          )}
        {!!html && (
          <>
            <Paper
              sx={{
                display: "grid",
                gridTemplateRows: "max-content 1fr",
                flex: 1,
                minWidth: "300px",
                maxHeight: "270px",
                p: 3,
                overflow: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">MJML Code</Typography>
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Copy MJML to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        void copyToClipboard(html, "MJML");
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download MJML file">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const baseFileName = getBaseFileName(originalFileName);
                        downloadAsFile(
                          html,
                          `${baseFileName}-rendered.mjml`,
                          "text/xml",
                        );
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
              <Box
                component="pre"
                sx={{
                  fontSize: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  overflow: "auto",
                  margin: 0,
                  p: 3,
                }}
              >
                {html}
              </Box>
            </Paper>

            <Paper
              sx={{
                display: "grid",
                gridTemplateRows: "max-content 1fr",
                flex: 1,
                minWidth: "300px",
                maxHeight: "270px",
                p: 3,
                overflow: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Project Data (JSON)</Typography>
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Copy JSON to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        void copyToClipboard(projectData, "JSON");
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Project Data as JSON">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const baseFileName = getBaseFileName(originalFileName);
                        downloadAsFile(
                          projectData,
                          `${baseFileName}-final-project.json`,
                          "application/json",
                        );
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
              <Box
                component="pre"
                sx={{
                  fontSize: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  overflow: "auto",
                  margin: 0,
                  p: 3,
                }}
              >
                {projectData}
              </Box>
            </Paper>

            <Paper
              sx={{
                display: "grid",
                gridTemplateRows: "max-content 1fr",
                flex: 1,
                minWidth: "300px",
                maxHeight: "270px",
                p: 3,
                overflow: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Rendered HTML</Typography>
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Copy HTML to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        void copyToClipboard(mjmlHtml, "HTML");
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Rendered HTML">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const baseFileName = getBaseFileName(originalFileName);
                        downloadAsFile(
                          mjmlHtml,
                          `${baseFileName}-rendered.html`,
                          "text/html",
                        );
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
              <Box
                component="pre"
                sx={{
                  fontSize: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  overflow: "auto",
                  margin: 0,
                  p: 3,
                }}
              >
                {mjmlHtml}
              </Box>
            </Paper>
          </>
        )}
      </Stack>
      {studioEditorOptions && <StudioEditor options={studioEditorOptions} />}
    </Box>
  );
}
